
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { imageCacheService } from '@/core/services/ImageCacheService';
import { Skeleton } from '@/components/ui/skeleton';
import { ImageIcon, AlertCircle } from 'lucide-react';
import { debugLogger } from '@/utils/debugLogger';

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  fallbackClassName?: string;
  onLoad?: () => void;
  onError?: (error: Error) => void;
  rootMargin?: string;
  threshold?: number;
  placeholder?: React.ReactNode;
  errorPlaceholder?: React.ReactNode;
  forceRefresh?: boolean;
}

export const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  className = '',
  fallbackClassName = '',
  onLoad,
  onError,
  rootMargin = '50px',
  threshold = 0.1,
  placeholder,
  errorPlaceholder,
  forceRefresh = false
}) => {
  const [imageState, setImageState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [imageSrc, setImageSrc] = useState<string>('');
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Intersection Observer setup
  useEffect(() => {
    const currentImg = imgRef.current;
    if (!currentImg) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observerRef.current?.unobserve(currentImg);
        }
      },
      { rootMargin, threshold }
    );

    observerRef.current.observe(currentImg);

    return () => {
      if (observerRef.current && currentImg) {
        observerRef.current.unobserve(currentImg);
      }
    };
  }, [rootMargin, threshold]);

  // Load image when in view
  useEffect(() => {
    if (!isInView || !src) return;

    let isMounted = true;

    const loadImage = async () => {
      try {
        setImageState('loading');
        const cachedUrl = await imageCacheService.getImage(src, forceRefresh);
        
        if (isMounted) {
          setImageSrc(cachedUrl);
        }
      } catch (error) {
        debugLogger.error('[LazyImage] Failed to load image:', error);
        if (isMounted) {
          setImageState('error');
          onError?.(error as Error);
        }
      }
    };

    loadImage();

    return () => {
      isMounted = false;
    };
  }, [isInView, src, forceRefresh, onError]);

  // Handle image load success
  const handleImageLoad = useCallback(() => {
    setImageState('loaded');
    onLoad?.();
  }, [onLoad]);

  // Handle image load error
  const handleImageError = useCallback(() => {
    setImageState('error');
    onError?.(new Error('Failed to load image'));
  }, [onError]);

  // Render loading placeholder
  const renderPlaceholder = () => {
    if (placeholder) {
      return placeholder;
    }

    return (
      <div className={`flex items-center justify-center bg-black/20 ${fallbackClassName}`}>
        <Skeleton className="w-full h-full bg-white/10" />
      </div>
    );
  };

  // Render error placeholder
  const renderError = () => {
    if (errorPlaceholder) {
      return errorPlaceholder;
    }

    return (
      <div className={`flex flex-col items-center justify-center bg-black/20 text-white/60 ${fallbackClassName}`}>
        <AlertCircle className="h-8 w-8 mb-2" />
        <span className="text-sm">Erro ao carregar</span>
      </div>
    );
  };

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Placeholder/Error states */}
      {imageState === 'loading' && renderPlaceholder()}
      {imageState === 'error' && renderError()}
      
      {/* Actual image */}
      <img
        ref={imgRef}
        src={imageSrc}
        alt={alt}
        className={`transition-opacity duration-300 ${
          imageState === 'loaded' ? 'opacity-100' : 'opacity-0'
        } ${className}`}
        onLoad={handleImageLoad}
        onError={handleImageError}
        style={{ 
          position: imageState === 'loaded' ? 'static' : 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }}
      />
    </div>
  );
};

export default LazyImage;
